// Add-on button: single click-to-add for single-variant products, or a modal
// (swatch grid for Colour options, list for everything else) when the product
// has multiple eligible variants.
if (!customElements.get('product-addon-button')) {
  class ProductAddonButton extends HTMLElement {
    connectedCallback () {
      this.button = this.querySelector('button')
      this.productId = this.dataset.productId
      this.productHandle = this.dataset.productHandle
      this.productTitle = this.dataset.productTitle
      this.hasMultipleVariants = this.dataset.hasMultipleVariants === 'true'
      this.isColourOption = this.dataset.isColourOption === 'true'
      this.variantIds = this.dataset.variantIds ? this.dataset.variantIds.split(',').filter(Boolean) : []
      this.optionsName = this.dataset.optionsName
      this.cardId = this.dataset.cardId
      this.card = this.closest('[data-product-addon-card]')

      if (this.hasMultipleVariants) {
        this.createModal()
      }

      if (this.button) this.init()
    }

    injectStylesOnce () {
      if (document.getElementById('product-addon-modal-styles')) return
      const style = document.createElement('style')
      style.id = 'product-addon-modal-styles'
      style.textContent = `
        .product-addon-modal[data-open='false'] {
          visibility: hidden;
          opacity: 0;
          pointer-events: none;
          transition: visibility 0s linear 0.2s, opacity 0.2s;
        }

        .product-addon-modal[data-open='true'] {
          visibility: visible;
          opacity: 1;
          pointer-events: auto;
          transition: visibility 0s linear 0s, opacity 0.2s;
        }

        @media only screen and (max-width: 900px) {
          .product-addon-modal .product-addon-modal__content {
            transform: translateY(100%);
            transition: transform 0.2s 0s ease;
          }

          .product-addon-modal[data-open='true'] .product-addon-modal__content {
            transform: translateY(0);
            transition: transform 0.2s 0.1s ease;
          }
        }

        .product-addon-modal svg {
          width: 100%;
          height: 100%;
        }

        .product-addon-modal__swatch-colour-outer {
          background: #fff;
          border: 1px solid #dad7d0;
          border-radius: 9999px;
        }
      `
      document.head.appendChild(style)
    }

    createModal () {
      this.injectStylesOnce()

      this.modal = document.createElement('div')
      this.modal.className = 'product-addon-modal relative z-99999999999'
      this.modal.setAttribute('data-open', 'false')

      this.modal.innerHTML = `
        <div class="product-addon-modal__container fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div class="product-addon-modal__content overscroll-contain bg-white rounded-lg shadow-[0px_2px_8px_0px_rgba(20,15,10,0.06),0px_8px_24px_0px_rgba(20,15,10,0.08)] max-h-[80vh] min-h-30 max-w-120 min-w-75 w-full md:w-auto overflow-hidden flex flex-col p-4 gap-4 fixed md:relative bottom-0 md:bottom-auto rounded-b-none md:rounded-b-lg">
            <div class="product-addon-modal__header flex items-center gap-4 w-full select-none">
              <div class="product-addon-modal__title inline-flex items-center text-lg font-semibold min-h-6 min-w-0 flex-1"></div>
              <button type="button" class="tb-icon product-addon-modal__close shrink-0 text-xl leading-none w-9 h-9 p-1 flex items-center justify-center bg-oat backdrop-blur-sm rounded-lg text-orange cursor-pointer" aria-label="${window.cartStrings?.variantModalCloseLabel || 'Close'}">
                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <div class="product-addon-modal__body-wrapper overflow-y-auto grow relative">
              <div class="product-addon-modal__options"></div>
            </div>
          </div>
        </div>
      `

      document.body.appendChild(this.modal)

      const container = this.modal.querySelector('.product-addon-modal__container')
      container.addEventListener('click', (e) => {
        if (e.target === container) this.hideModal()
      })

      this.modal.querySelector('.product-addon-modal__close').addEventListener('click', () => {
        this.hideModal()
      })

      this.handleEscapeKey = (e) => {
        if (e.key === 'Escape' && this.modal.getAttribute('data-open') === 'true') {
          this.hideModal()
        }
      }
      document.addEventListener('keydown', this.handleEscapeKey)
    }

    init () {
      this.button.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()

        if (this.hasMultipleVariants) {
          this.handleMultipleVariants()
        } else {
          this.addSingleVariantToCart()
        }
      })
    }

    getEmbeddedVariantData () {
      const script = this.querySelector('script[data-addon-variant-data]')
      if (!script) return null
      try {
        return JSON.parse(script.textContent)
      } catch (error) {
        return null
      }
    }

    async handleMultipleVariants () {
      this.enableLoading()

      try {
        const embeddedData = this.getEmbeddedVariantData()
        if (!embeddedData || !embeddedData.variants?.length) {
          this.showError(window.cartStrings?.variantModalLoadError || 'Failed to load product variants')
          return
        }

        const variants = embeddedData.variants.filter(v => v.available !== false)

        if (variants.length === 1) {
          await this.addVariantToCart(variants[0].id)
          return
        }

        if (embeddedData.isColourOption) {
          this.openColourModal(variants, embeddedData.optionsName)
        } else {
          this.openListModal(variants, embeddedData.optionsName)
        }
      } finally {
        this.disableLoading()
      }
    }

    setModalTitle (optionsName) {
      const titleEl = this.modal.querySelector('.product-addon-modal__title')
      if (titleEl && window.cartStrings?.variantModalTitle) {
        titleEl.textContent = window.cartStrings.variantModalTitle.replace('[option_name]', optionsName)
      }
    }

    openListModal (variants, optionsNameOverride) {
      const optionsName = optionsNameOverride || this.optionsName
      this.setModalTitle(optionsName)

      const optionsEl = this.modal.querySelector('.product-addon-modal__options')

      let rows = ''
      variants.forEach((variant) => {
        const priceNum = parseFloat(variant.price)
        const capNum = variant.compareAtPrice !== null && variant.compareAtPrice !== undefined ? parseFloat(variant.compareAtPrice) : NaN
        const showCompare = Number.isFinite(priceNum) && Number.isFinite(capNum) && capNum > priceNum

        const compareHtml = showCompare
          ? `<span class="line-through text-sm font-normal text-alizarin pointer-events-none">${this.formatPrice(variant.compareAtPrice)}</span>`
          : ''

        rows += `
          <button
            type="button"
            class="product-addon-modal__variant-button flex border border-clay rounded items-center justify-between gap-2 py-[9px] px-[13px] hover:bg-oat transition-colors w-full"
            data-variant-id="${variant.id}"
          >
            <span class="text-sm grow text-left pointer-events-none">${variant.title}</span>
            <span class="flex flex-wrap items-center justify-end gap-1.5 min-w-0 pointer-events-none">
              <span class="text-sm font-semibold text-black">${this.formatPrice(variant.price)}</span>
              ${compareHtml}
            </span>
          </button>
        `
      })

      optionsEl.innerHTML = `<div class="grid grid-cols-1 w-full gap-2">${rows}</div>`

      this.bindOptionClicks(optionsEl)
      this.showModal()
    }

    openColourModal (variants, optionsNameOverride) {
      const optionsName = optionsNameOverride || this.optionsName
      this.setModalTitle(optionsName)

      const optionsEl = this.modal.querySelector('.product-addon-modal__options')

      let swatches = ''
      variants.forEach((variant) => {
        const label = variant.title
        const colour = variant.colourKey || '#dad7d0'
        swatches += `
          <button
            type="button"
            class="product-addon-modal__swatch flex flex-col items-center gap-2 border border-clay rounded px-[13px] py-[9px] grow basis-21 min-w-21 max-w-35 hover:border-iron transition-colors"
            data-variant-id="${variant.id}"
          >
            <span class="product-addon-modal__swatch-colour-outer flex items-center justify-center w-6 h-6 shrink-0" aria-hidden="true">
              <span class="block w-4.5 h-4.5 rounded-full" style="background-color:${colour}"></span>
            </span>
            <span class="text-sm pointer-events-none whitespace-nowrap overflow-hidden text-ellipsis max-w-full">${label}</span>
          </button>
        `
      })

      optionsEl.innerHTML = `<div class="flex gap-2 items-start flex-wrap">${swatches}</div>`

      this.bindOptionClicks(optionsEl)
      this.showModal()
    }

    bindOptionClicks (optionsEl) {
      const existingHandler = this.variantClickHandler
      if (existingHandler) {
        optionsEl.removeEventListener('click', existingHandler)
      }

      this.variantClickHandler = (e) => {
        const target = e.target.closest('[data-variant-id]')
        if (!target) return
        const variantId = target.dataset.variantId
        this.hideModal()
        this.addVariantToCart(variantId)
      }

      optionsEl.addEventListener('click', this.variantClickHandler)
    }

    showModal () {
      if (!this.modal) return
      this.modal.setAttribute('data-open', 'true')
      if (!document.body.style.overflow) {
        document.body.style.overflow = 'hidden'
      }
    }

    hideModal () {
      if (!this.modal) return
      this.modal.setAttribute('data-open', 'false')
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = ''
      }
    }

    async addSingleVariantToCart () {
      const input = this.querySelector('[data-single-variant-id]')
      const variantId = input?.value
      if (variantId) await this.addVariantToCart(variantId)
    }

    async addVariantToCart (variantId) {
      this.enableLoading()

      const cartDrawer = document.querySelector('cart-drawer')

      try {
        const sectionIds = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
          ? cartDrawer.getSectionsToRender().map(section => section.id)
          : ['cart-drawer', 'cart-icon-bubble']

        const cartAddPayload = {
          id: variantId,
          quantity: 1,
          sections: sectionIds,
        }
        // eslint-disable-next-line camelcase -- Shopify Cart API field name
        cartAddPayload.sections_url = window.location.pathname

        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cartAddPayload),
        })

        const parsed = await response.json()
        if (!response.ok || parsed.status) {
          throw new Error(parsed.description || window.cartStrings?.variantModalAddError)
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty')
          cartDrawer.renderContents(parsed)
        }

        window.dispatchEvent(new CustomEvent('cart:updated', { detail: parsed }))

        this.updateInCartState()
      } catch (error) {
        console.error('[Product Addon] Error adding to cart:', error)
      } finally {
        this.disableLoading()
      }
    }

    updateInCartState () {
      if (!this.card) return

      const currentQuantity = parseInt(this.card.dataset.inCartQuantity, 10) || 0
      const newQuantity = currentQuantity + 1
      this.card.dataset.inCartQuantity = newQuantity

      const textEl = this.card.querySelector('[data-in-cart-text]')
      const template = textEl?.dataset.inCartTextTemplate
      if (textEl && template) {
        textEl.textContent = template.replace('[count]', newQuantity)
      }
    }

    showError (message) {
      console.error(`[Product Addon] ${message}`)
    }

    formatPrice (priceInCents) {
      if (window.FinanceUtils?.formatMoneyFromCents) {
        return window.FinanceUtils.formatMoneyFromCents(priceInCents)
      }
      const priceNum = parseFloat(priceInCents) / 100
      return `£${priceNum.toFixed(2)}`
    }

    enableLoading () {
      this.setAttribute('loading', 'true')
      if (this.button) {
        this.button.disabled = true
        this.button.setAttribute('aria-busy', 'true')
      }
    }

    disableLoading () {
      this.removeAttribute('loading')
      if (this.button) {
        this.button.disabled = false
        this.button.setAttribute('aria-busy', 'false')
      }
    }

    disconnectedCallback () {
      if (this.modal) this.modal.remove()
      if (this.handleEscapeKey) document.removeEventListener('keydown', this.handleEscapeKey)
    }
  }

  customElements.define('product-addon-button', ProductAddonButton)
}
